import * as DialogPrimitive from "@radix-ui/react-dialog";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { Building2, SearchX, X } from "lucide-react";
import { useTran } from "@/hooks";
import { useAuthStore } from "@/features/auth";
import {
  getUnitTypeMeta,
  ORGANIZATION_TYPE_META,
} from "@/features/organization/utils/organization.helpers";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import OrgBreadcrumb from "@/components/common/OrgBreadcrumb";
import {
  buildOrgStatusInfoMap,
  findOrgPathWithStatus,
} from "@/components/common/organization.utils";
import { useDashboardFiltersStore } from "../hooks/useDashboardStore";
import { buildSelectedOrganizationEntries } from "../utils/hierarchy-picker";
import { buildScopedOrganizationPickerTree } from "../utils/dashboard-filter-options";
import { Badge } from "@/components/ui/badge";

const HIERARCHY_LABELS = {
  all: "All Organizations",
  company: "All Companies",
  businessUnit: "All Business Units",
  site: "All Sites",
  country: "All Countries",
};

const buildDescription = ({ hasSelection, hierarchy, t }) => {
  if (hasSelection) {
    return hierarchy === "country"
      ? t("Showing only the countries selected in the dashboard filters.")
      : t("Showing only the organizations selected in the dashboard filters.");
  }
  if (hierarchy === "all") {
    return t(
      "Showing all organizations available in the current dashboard scope.",
    );
  }
  const label = HIERARCHY_LABELS[hierarchy] || HIERARCHY_LABELS.all;

  return t("Showing {{label}} in the current dashboard filter lens.", {
    label,
  });
};

const EmptyState = ({ title, description }) => (
  <div className="rounded-lg border border-dashed border-border/70 bg-muted px-4 py-8 text-center">
    <span className="mx-auto inline-flex h-9 w-9 items-center justify-center rounded-md bg-card text-muted-foreground shadow-sm">
      <SearchX className="h-4 w-4" />
    </span>
    <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
    <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
      {description}
    </p>
  </div>
);

const LoadingState = () => (
  <div className="space-y-2.5">
    {Array.from({ length: 6 }).map((_, index) => (
      <div
        key={index}
        className="animate-pulse rounded-lg border border-border/70 bg-card p-3 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-muted/60" />
          <div className="min-w-0 flex-1 space-y-2.5">
            <div className="flex items-start justify-between gap-3">
              <div className="h-4 w-2/5 rounded-full bg-muted/60" />
              <div className="h-3 w-16 rounded-full bg-muted/60" />
            </div>
            <div className="h-3 w-3/5 rounded-full bg-muted/60" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const OrganizationRow = ({
  item,
  orgStatus,
  orgInactiveDate,
  pathWithStatus,
}) => {
  const meta =
    item.unitLevel === "country"
      ? ORGANIZATION_TYPE_META.country
      : getUnitTypeMeta(item.unitLevel);
  const Icon = meta.icon || Building2;
  const hierarchyLabel =
    item.fullHierarchyLabel ||
    [item.parentHierarchyLabel, item.name].filter(Boolean).join(" > ");

  return (
    <div className="rounded-lg border border-border/70 bg-card p-3 shadow-sm">
      <div className="flex items-start gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border ${meta.color}`}
        >
          <Icon className="h-4 w-4" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-1.5 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="truncate text-sm font-semibold text-foreground">
                  {item.name}
                </p>
                <OrgStatusTag
                  status={orgStatus}
                  inactiveDate={orgInactiveDate}
                  className="text-[10px] shrink-0"
                />
              </div>
              <OrgBreadcrumb
                pathWithStatus={pathWithStatus}
                fallbackLabel={hierarchyLabel}
                className="mt-0.5"
              />
            </div>

            {/* <p className="mt-1 text-[9px] uppercase tracking-wide text-muted-foreground md:mt-0">
              {t(meta.label || item.unitLevel || "Organization")}
            </p> */}
            {/* {countryLabel ? (
              <Badge
                variant="secondary"
                className="inline-flex items-center gap-1 text-[11px] font-normal"
              >
                <Globe2 className="h-3 w-3" />
                {countryLabel}
              </Badge>
            ) : null} */}
          </div>
        </div>
      </div>
    </div>
  );
};

const SelectedOrganizationsDrawer = observer(({ open, onOpenChange }) => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const { filters, available, loading } = useDashboardFiltersStore();

  const scopedOrganizationTree = useMemo(
    () =>
      buildScopedOrganizationPickerTree({
        orgTree: available.orgTree || [],
        reportingYears: available.reportingYears || [],
        selectedYears: filters.years,
        companyId: user?.companyId,
        hierarchy: filters.hierarchy,
      }),
    [
      available.orgTree,
      available.reportingYears,
      filters.hierarchy,
      filters.years,
      user?.companyId,
    ],
  );

  const orgStatusMap = useMemo(
    () => buildOrgStatusInfoMap(available.orgTree || []),
    [available.orgTree],
  );

  const rows = useMemo(
    () =>
      buildSelectedOrganizationEntries(
        scopedOrganizationTree,
        filters.hierarchy,
        filters.orgIds || [],
      ),
    [filters.hierarchy, filters.orgIds, scopedOrganizationTree],
  );

  const rowsWithPath = useMemo(
    () =>
      rows.map((item) => ({
        ...item,
        pathWithStatus: available.orgTree
          ? findOrgPathWithStatus(available.orgTree, item.id)
          : [],
      })),
    [rows, available.orgTree],
  );

  const hasSelection = (filters.orgIds?.length || 0) > 0;
  const showLoading = open && loading.orgs && rows.length === 0;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-background/80 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          onPointerDownOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          className="fixed inset-y-0 right-0 z-[80] flex h-[100dvh] w-full max-w-[min(100vw,720px)] flex-col overflow-hidden border-l border-border/70 bg-background shadow-2xl outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-right-8 data-[state=open]:duration-300 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-200"
        >
          <div className="sticky top-0 z-20 border-b border-border/70 bg-background px-4 py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <DialogPrimitive.Title className="text-base font-semibold tracking-tight text-foreground">
                    {filters.hierarchy == "country"
                      ? t("Selected Countries")
                      : t("Selected Organizations")}
                  </DialogPrimitive.Title>
                  <Badge variant="secondary">{rows.length}</Badge>
                </div>
                <DialogPrimitive.Description className="mt-1 text-xs leading-5 text-muted-foreground">
                  {buildDescription({
                    hasSelection,
                    hierarchy: filters.hierarchy,
                    t,
                  })}
                </DialogPrimitive.Description>
              </div>

              <DialogPrimitive.Close className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-card text-muted-foreground shadow-sm transition hover:border-border hover:text-foreground">
                <X className="h-5 w-5" />
                <span className="sr-only">{t("Close")}</span>
              </DialogPrimitive.Close>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            <div className="space-y-3">
              {/* <div className="rounded-lg border border-border/70 bg-card p-3 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
                      <Network className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-base font-semibold text-foreground">
                        {t("Organizations in scope")}
                      </p>
                      <p className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                        {hasSelection
                          ? t("Based on the current organization selection")
                          : t("Based on the current hierarchy lens")}
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-2xl font-semibold tracking-tight text-foreground">
                      {rows.length}
                    </p>
                    <p className="text-xs text-muted-foreground">{t("rows")}</p>
                  </div>
                </div>
              </div> */}

              {showLoading ? (
                <LoadingState />
              ) : rows.length === 0 ? (
                <EmptyState
                  title={t("No organizations available")}
                  description={t(
                    "Adjust the hierarchy filter or organization selection to inspect another scope.",
                  )}
                />
              ) : (
                <div className="space-y-2.5">
                  {rowsWithPath.map((item) => {
                    const info = orgStatusMap.get(String(item.id));
                    return (
                      <OrganizationRow
                        key={String(item.id)}
                        item={item}
                        orgStatus={info?.status}
                        orgInactiveDate={info?.inactiveDate}
                        pathWithStatus={item.pathWithStatus}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
});

export default SelectedOrganizationsDrawer;
